import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ProductoPasarelaService} from '../../../_services/producto-pasarela.service';
import {ProductoPasarela} from '../../../_models/producto-pasarela';
import {Producto} from '../../../_models/producto';
import {NavService} from '../../../_services/_nav.service';
import {Listas} from '../../../_enums/enum';
import {ListaService} from '../../../_services/lista.service';
import {forkJoin, Observable} from 'rxjs';
import {ListaItem} from '../../../_models/lista-item';
import {ConfirmationService} from 'primeng/api';
import {AppSettingService} from '../../../_services/app-setting.service';
import {ProductoPasarelaTarifasComponent} from './producto-pasarela-tarifas/producto-pasarela-tarifas.component';
import {ProductoPasarelaContratosComponent} from './producto-pasarela-contratos/producto-pasarela-contratos.component';

@Component({
  selector: 'app-producto-pasarela',
  templateUrl: './producto-pasarela.component.html',
  styleUrl: './producto-pasarela.component.css'
})
export class ProductoPasarelaComponent implements OnInit {
  @Input() isDetail: boolean = false;
  @Input() producto: Producto;
  isLoading: boolean = true;
  records: ProductoPasarela[] = [];
  methods: ListaItem[] = [];
  cols: any[] = [];
  rows: number = 10;

  selectedMethod: ProductoPasarela;
  form: FormGroup;
  submitted: boolean = false;
  saving: boolean = false;
  cdKey: string = 'producto-pasarela';
  accordionActiveIndex: number = -1;
  @ViewChild('tarifasComponent') tarifasComponent: ProductoPasarelaTarifasComponent;
  @ViewChild('contratosComponent') contratosComponent: ProductoPasarelaContratosComponent;

  constructor(
    private fb: FormBuilder,
    private service: ProductoPasarelaService,
    private listService: ListaService,
    private navService: NavService,
    private confirmationService: ConfirmationService,
    public appSettingService: AppSettingService,
  ) {}

  ngOnInit(): void {
    this.cols = [
      { field: 'codigoMetodoLista', header: this.navService.buscarMensaje('PRODUCTO_PASARELA.LBL_CODIGO'), sort: false },
      { field: 'nombreMetodoLista', header: this.navService.buscarMensaje('PRODUCTO_PASARELA.LBL_METODO'), sort: false },
      { field: 'acciones', header: this.navService.buscarMensaje('LBL_ACCIONES'), sort: false },
    ];

    this.loadData();
  }

  loadData(): void {
    forkJoin([
      this.service.getByIdProducto(this.producto.idProducto),
      this.listService.getMasterDetails(Listas.LISTAS_TIPOS_METODOS_PASARELA),
    ]).subscribe({
      next: ([records, methods]) => {
        this.records = records;
        this.methods = methods;
        this.isLoading = false;
      },
    });
  }

  loadRecords(): void {
    this.isLoading = true;
    this.service.getByIdProducto(this.producto.idProducto).subscribe({
      next: (records) => {
        this.records = records;
        this.isLoading = false;
      },
    });
  }

  onSelectMethod(method: ListaItem): void {
    if(this.selectedMethod?.idMetodoLista === method.idLista && this.form?.enabled) return;

    this.confirmIfDirty(() => {
      let existingRecord: ProductoPasarela | undefined = this.records.find(e => e.idMetodoLista === method.idLista);

      if (!existingRecord) {
        existingRecord = {
          idProducto: this.producto.idProducto,
          idMetodoLista: method.idLista,
        } as ProductoPasarela;
      }

      this.isDetail ? this.view(existingRecord) : this.edit(existingRecord);
    });
  }

  edit(record: ProductoPasarela): void {
    this.selectedMethod = record;
    this.accordionActiveIndex = -1;
    this.buildForm();
  }

  view(record: ProductoPasarela): void {
    this.selectedMethod = record;
    this.accordionActiveIndex = -1;
    this.buildForm();
    this.form.disable();
  }

  onCancel(): void {
    this.confirmIfDirty(() => this.selectedMethod = null);
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.form.invalid) return;

    this.saving = true;
    const record: ProductoPasarela = this.form.value;
    const request: Observable<ProductoPasarela> = this.isNewRecord ? this.service.create(record) : this.service.update(record);
    request.subscribe({
      next: () => {
        this.navService.setMesage(this.isNewRecord ? 1 : 2);
        this.saving = false;
        this.selectedMethod = null;
        this.loadRecords();
      },
      error: () => {
        this.navService.setMesage(3);
        this.saving = false;
      },
    });
  }

  get isNewRecord(): boolean {
    return !this.selectedMethod?.idProductoPasarela;
  }

  get f() {
    return this.form.controls;
  }

  private buildForm(): void {
    this.submitted = false;
    this.form = this.fb.group({
      idProducto: [this.selectedMethod ? this.selectedMethod.idProducto : null],
      idProductoPasarela: [this.selectedMethod ? this.selectedMethod.idProductoPasarela : null],
      idMetodoLista: [this.selectedMethod ? this.selectedMethod.idMetodoLista : null],
      tarifaGeneral: [this.selectedMethod ? this.selectedMethod.tarifaGeneral : null, [Validators.required]],
      indicadorAplicaIva: [this.selectedMethod ? this.selectedMethod.indicadorAplicaIva : false],
      valorIva: [
        this.selectedMethod ? this.selectedMethod.valorIva : null,
        this.selectedMethod?.indicadorAplicaIva ? [Validators.required] : []
      ],
      indicadorAplicaTasaPreferencial: [this.selectedMethod ? this.selectedMethod.indicadorAplicaTasaPreferencial : false],
    });

    this.f.indicadorAplicaIva.valueChanges.subscribe((indicadorAplicaIva: boolean) => {
      if (indicadorAplicaIva) {
        this.f.valorIva.setValidators([Validators.required]);
      } else {
        this.f.valorIva.setValidators([]);
        this.f.valorIva.setValue(null);
      }
      this.f.valorIva.updateValueAndValidity();
    });
  }

  private confirmIfDirty(action: () => void): void {
    const isOtherFormsDirty: boolean = this.tarifasComponent?.isFormDirty || this.contratosComponent?.isFormDirty;
    if(this.selectedMethod && (this.form?.dirty || isOtherFormsDirty)) {
      this.confirmationService.confirm({
        key: this.cdKey,
        header: this.navService.buscarMensaje('LBL_CAMBIOS_SIN_GUARDAR'),
        message: this.navService.buscarMensaje('MSG_CAMBIOS_SIN_GUARDAR'),
        accept: action,
      });
    } else {
      action();
    }
  }
}
